import { float, normalize } from 'three/tsl';
import { VERDANT_AIR } from '../../engine/atmosphere/AtmosphereDef';
import { VERDANT_CLOUDS } from '../../engine/atmosphere/CloudLayer';
import { VERDANT_TERRAIN } from '../../engine/terrain/TerrainProfile';
import { VERDANT_STYLE } from '../../engine/terrain/TerrainStyle';
import { VERDANT_GRASS, VERDANT_TREES } from '../../engine/vegetation/VegetationStyle';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { boulders } from './rocks';

/** The showcase hollow: 12 km radius, lush terrain, a sun at the centre wearing a turning veil. */
export const VERDANT: HollowSceneDef = {
  id: 'verdant',
  name: 'Verdant',
  center: [-4.2e6, 8.7e6, 3.1e6],
  radius: 12_000,
  seed: 7,
  sun: { radius: 180, color: [1.0, 0.91, 0.78], intensity: 5.5 },
  terrain: VERDANT_TERRAIN,
  air: VERDANT_AIR,
  clouds: VERDANT_CLOUDS,
  veil: {
    litFraction: 0.46,
    softness: 0.09,
    dayLength: 600,
    spinAxis: [0.25, 1, 0.1],
    duskColor: [1.0, 0.44, 0.2],
    // Night is the sun gone: bounce light is even over a sphere, so it stays, only cooler.
    nightAmbient: [0.8, 0.95, 1.4],
  },
  worldAlbedo: [0.15, 0.18, 0.17],
  style: () => VERDANT_STYLE,
  grass: () => VERDANT_GRASS,
  trees: () => VERDANT_TREES,
  rocks: () =>
    boulders(
      [
        [0.24, 0.23, 0.21],
        [0.34, 0.32, 0.29],
      ],
      [
        [0.13, 0.15, 0.1],
        [0.19, 0.2, 0.15],
      ],
    ),
  // A meadow above a lake, facing a snow peak (found by the photo tour's viewpoint search).
  spawn: { dir: [0.4466, -0.7094, 0.5452], minHeight: 20, maxHeight: 90, face: [0.6262, -0.6706, 0.3977] },
  // Flocks of birds circling over the land by day.
  birds: (rig) => ({
    flocks: 12,
    perFlock: 7,
    altitude: [25, 80],
    radius: [35, 90],
    speed: 11,
    span: 1.3,
    color: [0.06, 0.06, 0.07],
    visible: () => rig.veilAt(normalize(rig.centerRel.negate())),
  }),
  // Fireflies over the meadows at night.
  motes: (rig) => ({
    count: 1200,
    radius: 35,
    size: [0.07, 0.13],
    colors: [
      [0.85, 1.0, 0.35],
      [1.0, 0.85, 0.3],
    ],
    brightness: 5,
    drift: 0.2,
    glow: () =>
      float(1)
        .sub(rig.veilAt(normalize(rig.centerRel.negate())))
        .pow(2),
  }),
  exposureKey: 0.42,
  hints: ['Look up: the sky is the far side of your world.', 'Hold T to watch a day pass. V to fly.'],
  waterSkyTint: [0.8, 0.95, 1.3],
  ambience: {
    wind: { gain: 0.9, cutoff: 650 },
    events: [
      { kind: 'bird', rate: 0.3, gain: 0.045, when: 'day' },
      { kind: 'cricket', rate: 1.4, gain: 0.018, when: 'night' },
    ],
  },
};
